import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import {FC, useState, useEffect} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Button} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {getAllHSCategories, importDataMapping} from './core/_requests'

import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {ListHSCategoriesRow} from './core/_models'
import {Link} from 'react-router-dom'
import {ListHSCategoriesTable} from './components/ListHSCategoriesTable'
import './MasterDataHSPage.scss'
import {exportTemplate} from '../master-data-hs-detail/core/_requests'
import ModalImportHSCategoriesMapping from './components/ModalImportHSCategoriesMapping'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'

const ReactSwal = withReactContent(Swal)

const MasterDataHSPage: FC<{subject: string}> = ({subject}) => {
  const intl = useIntl()
  const ability = useAbility(AbilityContext)
  const axiosPrivate = useAxiosPrivate()
  const [isLoading, setIsLoading] = useState(false)
  const [listHSCategories, setListHSCategories] = useState<Array<ListHSCategoriesRow> | null>()

  const [countData, setCountData] = useState(0)

  // ---- import modal state ----
  const [showImportModal, setShowImportModal] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [isImporting, setIsImporting] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  // ----------------------------

  const fetchListHSCategories = () => {
    setIsLoading(true)

    getAllHSCategories(axiosPrivate)
      .then((res) => {
        if (res.data.count > 0) {
          setCountData(res.data.count)
          setListHSCategories(res.data.table_data)
        }
      })
      .catch((e) => {
        console.error(e)
        setIsLoading(false)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    fetchListHSCategories()
  }, [])

  // Export Template
  const handleExportTemplate = () => {
    exportTemplate(axiosPrivate)
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'hs-category-template.xlsx')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      })
      .catch((err) => {
        console.error('Failed to download template:', err)
      })
  }
  // end of Export Template

  // Import Template – open modal
  const handleImportTemplate = () => {
    setShowImportModal(true)
    setImportError(null)
  }

  const handleCloseImportModal = () => {
    if (isImporting) return
    setShowImportModal(false)
    setImportFile(null)
    setImportError(null)
  }

  const handleSubmitImport = () => {
    if (!importFile) {
      setImportError('Please select a file before importing.')
      return
    }

    setIsImporting(true)
    setImportError(null)

    const formData = new FormData()
    formData.append('file', importFile)

    importDataMapping(axiosPrivate, formData)
      .then((res) => {
        if (res.status === 200) {
          setShowImportModal(false)
          ReactSwal.fire({
            title: 'Import Successful',
            icon: 'success',
            confirmButtonText: 'OK',
          }).then(() => {
            window.location.reload()
          })
        }
      })
      .catch((resErr: any) => {
        const message = resErr?.response?.data?.message || 'Upload failed. Please check your file.'
        setImportError(message)
      })
      .finally(() => {
        setIsImporting(false)
      })
  }
  // end of Import Template

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {intl.formatMessage({id: subject})}
      </PageTitle>
      <div className='MasterDataHSPage'>
        <Row className='mb-5'>
          <Col>
            <Card className='card-custom card-border'>
              <Card.Body className='d-flex align-items-center'>
                <div className='d-flex flex-column'>
                  <h3 className='text-primary'>{intl.formatMessage({id: subject})}</h3>
                  <div className='text-gray-700'>
                    In this module, you can manage <strong>HS Categories</strong> and map{' '}
                    <strong>HS Codes</strong> to each category.
                    <br />
                    <br />➤ Use <strong>Add</strong> to create a new category, or{' '}
                    <strong>Edit</strong> to modify an existing one through the{' '}
                    <strong>Detail</strong> page.
                    <br />
                    {ability.can('create', subject) ? (
                      <>
                        ➤ To assign HS Codes to categories in bulk:
                        <br />
                        &nbsp;&nbsp;&nbsp;&nbsp;• First, download the Excel format here{' '}
                        <Link
                          to=''
                          className='text-link'
                          onClick={() => {
                            handleExportTemplate()
                          }}
                        >
                          Export Template
                        </Link>
                        <br />
                        &nbsp;&nbsp;&nbsp;&nbsp;• Fill in the HS Code mapping
                        <br />
                        &nbsp;&nbsp;&nbsp;&nbsp;• Then upload it here
                        <Button
                          className='btn btn-primary btn-inline'
                          onClick={() => {
                            handleImportTemplate()
                          }}
                        >
                          Import Excel
                        </Button>
                        <br />
                        <i>*Both manual and bulk mapping are supported.</i>
                      </>
                    ) : (
                      <>
                        ➤ To assign HS Codes to categories in bulk, you need to have permission to
                        import Excel.
                      </>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row className='mb-5'>
          <Col>
            <Card>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-file-alt px-2'></span>
                      <h3 className='text-gray-700'>List HS Categories</h3>
                    </div>
                    {ability.can('create', subject) ? (
                      <div className='card-action'>
                        <Link
                          to={'/master-data-hs-categories/add'}
                          className='btn btn-primary d-flex gap-4 align-items-center'
                        >
                          <span className='fa fa-plus'></span>
                          Add
                        </Link>
                      </div>
                    ) : (
                      ''
                    )}
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <ListHSCategoriesTable
                  subject={subject}
                  onAgreementDelete={(deletedId) => {
                    setListHSCategories((prev) => prev?.filter((item) => item.id !== deletedId))
                  }}
                  rows={listHSCategories}
                  isLoading={isLoading}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
      {/* Import Modal */}
      <ModalImportHSCategoriesMapping
        show={showImportModal}
        onClose={handleCloseImportModal}
        onSubmit={handleSubmitImport}
        onFileChange={setImportFile}
        isSubmitting={isImporting}
        errorMessage={importError}
      />
    </>
  )
}

export default MasterDataHSPage
